/**
 * SectorAnalysisPage — dedicated page combining:
 *  1. GICS Sector & Sub-Sector Performance (11 sectors, 74 subsectors,
 *     10 timeframes: 1D / 5D / 10D / 20D / 50D / 100D / 200D / 1Y / 3Y / 5Y)
 *  2. Early-Warnings-style scoring pill per subsector
 *  3. On-demand AI Direction card (BUY/HOLD/SELL per timeframe) triggered
 *     by clicking a subsector row
 *  4. AI Chat box with session memory, research-backed citations, and
 *     optional allocation equity-curve chart vs SPY
 *
 * Session-only chat memory per user choice — resets on refresh.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  RefreshCw, ChevronDown, ChevronRight, Layers,
  TrendingUp, TrendingDown, MinusCircle,
  Sparkles, AlertTriangle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { API } from "../config/api";
import AIAnalysisChat from "../components/AIAnalysisChat";

// The 10 timeframe columns.  Kept in one array so headers, cells and the
// AI Direction grid all iterate the same list.
const TF_COLS = [
  { key: "r1d",   label: "1D"   },
  { key: "r5d",   label: "5D"   },
  { key: "r10d",  label: "10D"  },
  { key: "r20d",  label: "20D"  },
  { key: "r50d",  label: "50D"  },
  { key: "r100d", label: "100D" },
  { key: "r200d", label: "200D" },
  { key: "r1y",   label: "1Y"   },
  { key: "r3y",   label: "3Y"   },
  { key: "r5y",   label: "5Y"   },
];

const fmtRet = (v) => v == null ? "—"
  : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`;
const retCls = (v) => v == null ? "text-slate-500"
  : v >= 0 ? "text-emerald-400" : "text-rose-400";

export default function SectorAnalysisPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSector, setExpandedSector] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/sector-analysis/overview`, { timeout: 30000 });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="sector-analysis-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-2">
            <Layers className="w-7 h-7 text-purple-400" />
            Sector Analysis
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            GICS sector + subsector performance · Early Warnings scoring · AI Direction · Research-backed chat
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="sa-refresh-btn"
          className="inline-flex items-center gap-2 rounded-md border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && (
        <Card className="border-rose-700 bg-rose-950/30">
          <CardContent className="pt-4 flex items-center gap-2 text-rose-200 text-sm">
            <AlertTriangle className="w-4 h-4" />{error}
          </CardContent>
        </Card>
      )}

      <AIAnalysisChat
        mode="sector"
        extraContext={{
          sector_summary: (data?.sectors || []).map(s => ({
            name: s.name, etf: s.etf,
            r1d: s.returns?.r1d, r5d: s.returns?.r5d,
            r20d: s.returns?.r20d, r1y: s.returns?.r1y,
          })).slice(0, 11),
        }}
      />

      <SectorTable
        data={data}
        loading={loading}
        expandedSector={expandedSector}
        setExpandedSector={setExpandedSector}
      />
    </div>
  );
}

/* ── Sector performance table ─────────────────────────────────────────── */
function SectorTable({ data, loading, expandedSector, setExpandedSector }) {
  const sectors = data?.sectors || [];
  return (
    <Card className="border-slate-700 bg-slate-900/70">
      <CardHeader className="pb-2">
        <CardTitle className="text-base sm:text-lg text-white flex items-center gap-2 flex-wrap">
          <TrendingUp className="w-5 h-5 text-purple-400" />
          GICS Sector & Sub-Sector Performance
          <span className="text-xs text-slate-400 font-normal ml-auto">
            {sectors.length} sectors · click a row to expand subsectors · click a subsector for AI Direction
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm sm:text-base">
            <thead className="text-slate-400 border-b border-slate-800">
              <tr>
                <th className="px-3 py-3 text-left w-10"></th>
                <th className="px-3 py-3 text-left font-semibold">Sector / Sub-Sector</th>
                <th className="px-3 py-3 text-left w-20 font-semibold">ETF</th>
                {TF_COLS.map(c => (
                  <th key={c.key} className="px-2 py-3 text-right font-semibold min-w-[68px]">{c.label}</th>
                ))}
                <th className="px-3 py-3 text-right w-24 font-semibold">EW Score</th>
              </tr>
            </thead>
            <tbody>
              {loading && sectors.length === 0 && (
                <tr><td colSpan={14} className="p-8 text-center text-slate-500">
                  <Skeleton className="h-24 bg-slate-800/60" />
                </td></tr>
              )}
              {sectors.map(sec => (
                <SectorRow
                  key={sec.etf}
                  sec={sec}
                  expanded={expandedSector === sec.etf}
                  onToggle={() => setExpandedSector(expandedSector === sec.etf ? null : sec.etf)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function SectorRow({ sec, expanded, onToggle }) {
  return (
    <>
      <tr
        className="border-b border-slate-800/70 hover:bg-slate-800/40 cursor-pointer"
        onClick={onToggle}
        data-testid={`sa-sector-${sec.etf}`}
      >
        <td className="px-3 py-3 text-slate-400">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
        <td className="px-3 py-3 text-white font-bold text-base" style={{ color: sec.color || undefined }}>
          {sec.name}
        </td>
        <td className="px-3 py-3 font-mono text-slate-200 font-semibold">{sec.etf}</td>
        {TF_COLS.map(c => (
          <td key={c.key} className={`px-2 py-3 text-right font-mono font-semibold ${retCls(sec.returns?.[c.key])}`}>
            {fmtRet(sec.returns?.[c.key])}
          </td>
        ))}
        <td className="px-3 py-3 text-right"><EWScore ew={sec.ew} /></td>
      </tr>
      {expanded && (sec.sub_sectors || []).map(ss => (
        <SubsectorRow key={ss.etf + ss.name} ss={ss} parent={sec.name} />
      ))}
    </>
  );
}

function SubsectorRow({ ss, parent }) {
  const [openAI, setOpenAI] = useState(false);
  return (
    <>
      <tr
        className="border-b border-slate-900/60 bg-slate-950/40 hover:bg-slate-900/60 cursor-pointer"
        onClick={() => setOpenAI(!openAI)}
        data-testid={`sa-subsector-${ss.etf}`}
      >
        <td className="px-3 py-2.5 pl-8 text-purple-400">
          {openAI ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </td>
        <td className="px-3 py-2.5 text-slate-300 pl-6 text-sm">↳ {ss.name}</td>
        <td className="px-3 py-2.5 font-mono text-slate-300 font-semibold">{ss.etf}</td>
        {TF_COLS.map(c => (
          <td key={c.key} className={`px-2 py-2.5 text-right font-mono ${retCls(ss.returns?.[c.key])}`}>
            {fmtRet(ss.returns?.[c.key])}
          </td>
        ))}
        <td className="px-3 py-2.5 text-right"><EWScore ew={ss.ew} /></td>
      </tr>
      {openAI && (
        <tr>
          <td colSpan={14} className="p-0">
            <AIDirectionPanel ss={ss} parent={parent} />
          </td>
        </tr>
      )}
    </>
  );
}

function EWScore({ ew }) {
  if (!ew) return <span className="text-slate-600 text-xs">—</span>;
  const net = ew.net_pct ?? 0;
  const cls =
    net > 25  ? "text-emerald-300 bg-emerald-950/60 border-emerald-700" :
    net < -25 ? "text-rose-300 bg-rose-950/60 border-rose-700" :
                "text-amber-300 bg-amber-950/40 border-amber-700";
  return (
    <span className={`inline-flex items-center rounded border px-2 py-1 text-sm font-mono font-bold ${cls}`}
      title={`Recovery: ${ew.recovery_pct?.toFixed(1) ?? "?"}% · Deterioration: ${ew.deterioration_pct?.toFixed(1) ?? "?"}% · Breakout: ${ew.breakout_pct?.toFixed(1) ?? "?"}% · RS 20D vs SPY: ${ew.rs_20d_vs_spy?.toFixed(2) ?? "?"}%`}>
      {net >= 0 ? "+" : ""}{net?.toFixed(0)}
    </span>
  );
}

/* ── AI Direction (on-demand per subsector) ───────────────────────────── */
function AIDirectionPanel({ ss, parent }) {
  const [dir, setDir] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setBusy(true); setErr(null);
    axios.post(`${API}/sector-analysis/ai-direction`, {
      symbol: ss.etf, name: ss.name, parent, returns: ss.returns, ew: ss.ew,
    }, { timeout: 40000 })
      .then(r => { if (!cancelled) setDir(r.data); })
      .catch(e => { if (!cancelled) setErr(e?.response?.data?.detail || e?.message || "Failed"); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [ss.etf, ss.name, ss.returns, ss.ew, parent]);

  const sigBg = (s) =>
    s === "BUY"  ? "bg-emerald-950/60 border-emerald-700 text-emerald-300" :
    s === "SELL" ? "bg-rose-950/60 border-rose-700 text-rose-300" :
                   "bg-amber-950/40 border-amber-700 text-amber-300";
  const sigIcon = (s) =>
    s === "BUY"  ? <TrendingUp className="w-3 h-3" /> :
    s === "SELL" ? <TrendingDown className="w-3 h-3" /> :
                   <MinusCircle className="w-3 h-3" />;

  return (
    <div className="border-y border-purple-900/40 bg-purple-950/20 p-4 sm:p-5"
         data-testid={`sa-ai-direction-${ss.etf}`}>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-purple-400" />
        <span className="text-sm font-bold text-purple-200 uppercase tracking-wider">
          AI Direction — {ss.name} ({ss.etf})
        </span>
        {busy && <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-400 ml-2" />}
      </div>
      {err && <div className="text-sm text-rose-300 mb-2">{err}</div>}
      {dir && (
        <>
          <div className={`inline-flex items-start gap-2 rounded border px-3 py-2 text-sm font-mono font-semibold mb-4 ${sigBg(dir.summary?.overall_signal)} max-w-full`}>
            {sigIcon(dir.summary?.overall_signal)}
            <span className="shrink-0">Overall: {dir.summary?.overall_signal || "HOLD"}</span>
            <span className="text-slate-200 font-normal ml-2">
              {dir.summary?.key_insight}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {(dir.directions || []).map(d => (
              <div key={d.timeframe}
                   className={`rounded border p-3 ${sigBg(d.signal)}`}
                   data-testid={`sa-dir-${ss.etf}-${d.timeframe}`}>
                <div className="flex items-center justify-between text-sm font-bold">
                  <span>{d.timeframe}</span>
                  <span className="inline-flex items-center gap-1">
                    {sigIcon(d.signal)} {d.signal}
                  </span>
                </div>
                <div className="text-xs sm:text-sm mt-1.5 opacity-90 leading-snug">{d.rationale}</div>
                <div className="text-xs mt-2 font-mono opacity-75">
                  confidence {d.confidence}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {busy && !dir && <Skeleton className="h-32 bg-slate-800/60" />}
    </div>
  );
}

